import React, { useEffect, useState } from 'react';
import { Modal, Button, Spin } from 'antd';
import { Viewer } from '@toast-ui/react-editor';
import { toast } from 'react-toastify';
import { api } from '../../config/api';

const ExternalIorTermsPopup = ({ linkId, onLogout }) => {
    const [terms, setTerms] = useState(null); // null = loading
    const [showModal, setShowModal] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [termsLoaded, setTermsLoaded] = useState(false);

    // Unique storage key for this specific link
    const STORAGE_KEY = `external_terms_version_${linkId}`;

    // -------------------------------
    // Fetch the terms once
    // -------------------------------
    const loadTerms = async () => {
        try {
            // UPDATED: Use the new external link specific route
            const res = await api.get(`/external-links/${linkId}/terms-conditions?search=tradeos`);
            const { data } = res.data;

            if (!data || !data[0]) {
                // If no terms exist in DB, we don't block the user
                setTerms({ content: '', version: '0' });
                setTermsLoaded(true);
                return;
            }

            setTerms(data[0]);
            setTermsLoaded(true);
        } catch (err) {
            console.error(err);
            setTermsLoaded(true);
        }
    };

    useEffect(() => {
        if (linkId) {
            loadTerms();
        }
    }, [linkId]);

    // -------------------------------
    // Check Local Storage vs API Version
    // -------------------------------
    useEffect(() => {
        if (!termsLoaded || !terms || !terms.version) {
            return;
        }

        const storedVersion = localStorage.getItem(STORAGE_KEY);
        const currentVersion = String(terms.version); // Ensure both are strings

        if (storedVersion && storedVersion === currentVersion) {
            setShowModal(false);
            setIsNew(false);
        } else if (!storedVersion) {
            setShowModal(true);
            setIsNew(false);
        } else {
            setShowModal(true);
            setIsNew(true);
        }
    }, [terms, termsLoaded, linkId]);

    // -------------------------------
    // Accept Handler
    // -------------------------------
    const handleAccept = () => {
        if (terms && terms.version) {
            const versionToStore = String(terms.version);
            localStorage.setItem(STORAGE_KEY, versionToStore);
            setShowModal(false);
            setIsNew(false);
            toast.success("Terms accepted successfully.");
        }
    };

    // -------------------------------
    // Reject Handler
    // -------------------------------
    const handleReject = () => {
        toast.info("You must accept the Terms & Conditions to continue.");
        if (onLogout) {
            onLogout();
        }
    };

    // -------------------------------
    // Render Logic
    // -------------------------------
    // If we are supposed to show the modal but terms are loading, show a spinner overlay
    if (showModal && !termsLoaded) {
        return (
            <Modal open footer={null} closable={false} centered>
                <div className="w-full flex flex-col items-center justify-center p-10">
                    <Spin size="large" />
                    <p className="mt-4 text-gray-500">Loading Terms & Conditions...</p>
                </div>
            </Modal>
        );
    }

    if (!showModal) return null;

    return (
        <Modal
            open={showModal}
            centered
            width={900}
            closable={false}
            maskClosable={false} // Prevent clicking outside
            title={
                <div className="flex flex-col">
                    <h2 className="text-xl font-semibold">
                        {isNew
                            ? "We’ve Updated Our Terms & Conditions"
                            : "Terms & Conditions"}
                    </h2>

                    {isNew && (
                        <p className="text-gray-500 mt-1 font-normal text-sm">
                            Please review and accept the updated terms to continue using this link.
                        </p>
                    )}
                </div>
            }
            footer={[
                <Button key="decline" danger onClick={handleReject}>
                    I Decline (Logout)
                </Button>,
                <Button key="accept" type="primary" onClick={handleAccept}>
                    I Accept
                </Button>,
            ]}
        >
            <div className="h-[60vh] overflow-y-auto border p-4 rounded-md bg-gray-50">
                {/* Use Viewer to render HTML/Markdown content */}
                {terms?.content && <Viewer initialValue={terms.content} />}
            </div>
        </Modal>
    );
};

export default ExternalIorTermsPopup;